import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, usePage } from "@inertiajs/react"
import { IconCircleCheck, IconCircleDashed, IconClock } from "@tabler/icons-react"

interface Review {
  id: number
  reviewer_type: string
  reviewer_name: string
  status: string
  score: number | null
  submitted_at: string | null
  cycle: {
    id: number
    display_name: string
    user_name: string
  }
}

interface Props {
  [key: string]: unknown
  reviews: Review[]
}

function statusIcon(status: string) {
  switch (status) {
    case "completed":
      return <IconCircleCheck className="h-4 w-4 text-green-600" />
    case "in_progress":
      return <IconClock className="h-4 w-4 text-yellow-600" />
    default:
      return <IconCircleDashed className="h-4 w-4 text-muted-foreground" />
  }
}

function statusVariant(status: string): "default" | "secondary" | "outline" {
  switch (status) {
    case "completed":
      return "default"
    case "in_progress":
      return "secondary"
    default:
      return "outline"
  }
}

function statusLabel(status: string) {
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export default function PerformanceReviewsIndex() {
  const { reviews } = usePage<Props>().props

  return (
    <>
      <Head title="Performance Reviews - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Performance Reviews</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Performance Reviews</h1>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>Review Cycle</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Reviewer Type</TableHead>
              <TableHead>Reviewer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Score</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review.id}>
                <TableCell>{statusIcon(review.status)}</TableCell>
                <TableCell>
                  <a href={`/performance_review_cycles/${review.cycle.id}`} className="text-primary hover:underline">
                    {review.cycle.display_name}
                  </a>
                </TableCell>
                <TableCell className="text-muted-foreground">{review.cycle.user_name}</TableCell>
                <TableCell className="capitalize font-medium">{review.reviewer_type}</TableCell>
                <TableCell className="text-muted-foreground">{review.reviewer_name}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(review.status)}>{statusLabel(review.status)}</Badge>
                </TableCell>
                <TableCell>
                  {review.score ? (
                    <span className="font-medium">{review.score.toFixed(1)}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{formatDate(review.submitted_at)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" asChild>
                    <a href={`/performance_review_cycles/${review.cycle.id}/performance_reviews/${review.id}`}>View</a>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {reviews.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-muted-foreground text-center py-8">
                  No performance reviews found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  )
}

PerformanceReviewsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
